import { z } from 'zod';
import { UpdateSchoolDto, UpdateSchoolLogoParams } from '@/api/school/dto/updateSchoolDto';
import { classroomSummarySchema } from '@/api/schoolGroup';
import axiosInstance from '@/lib/axiosInstance';
import { Classroom, ClassroomWithStudentCount } from '@/model/Classroom.model';
import { getPaginatedItemsSchema, Paginated, PaginationQueryParams } from '@/model/common/pagination.model';
import { SortingQueryParams } from '@/model/common/sorting.model';
import { School } from '@/model/School/School.model';
import { SchoolSetting } from '@/model/SchoolSetting/SchoolSetting.model';
import { User, UserRole } from '@/model/User.model';

export interface GetSchoolQuery {
  name?: string;
}

const classroomSummariesSchema = z.object({
  classroomSummaries: z.array(classroomSummarySchema),
});

export const getSchools = async (
  query: GetSchoolQuery,
  pagination: PaginationQueryParams & SortingQueryParams
): Promise<Paginated<School>> => {
  const queryParams = new URLSearchParams();

  if (query.name) {
    queryParams.append('name', query.name);
  }

  queryParams.append('page', pagination.page.toString());
  queryParams.append('pageSize', pagination.pageSize.toString());
  queryParams.append('sortDirection', pagination.sortDirection);
  queryParams.append('sortBy', pagination.sortBy);
  const { data } = await axiosInstance.get(`schools`, {
    params: queryParams,
  });
  return getPaginatedItemsSchema(School.zodSchema).parse(data);
};

export const getSchoolById = async (id: string): Promise<School> => {
  const { data } = await axiosInstance.get(`schools/${id}`);
  return School.fromJson(data);
};

export const getSchoolClassroomSummary = async (id: string) => {
  const { data } = await axiosInstance.get(`schools/${id}/classroom-summary`);
  const validJson = classroomSummariesSchema.parse(data);

  return validJson;
};

export const getSchoolClassrooms = async (id: string): Promise<Classroom[]> => {
  const { data } = await axiosInstance.get(`schools/${id}/classrooms`);
  return Classroom.fromJsonArray(data);
};

export const getSchoolClassroomsWithStudentCount = async (id: string): Promise<ClassroomWithStudentCount[]> => {
  const { data } = await axiosInstance.get(`schools/${id}/classrooms-with-student-count`);
  return data;
};

export const getSchoolUsers = async (id: string, role?: UserRole): Promise<User[]> => {
  const queryParams = new URLSearchParams();
  if (role !== undefined) {
    queryParams.append('role', role);
  }
  const { data } = await axiosInstance.get(`schools/${id}/users?${queryParams.toString()}`);
  return User.fromJsonArray(data);
};

export const createSchool = async (name: string): Promise<School> => {
  const { data } = await axiosInstance.post('schools', { name });
  return School.fromJson(data);
};

export const updateSchool = async (id: string, dto: UpdateSchoolDto): Promise<School> => {
  const { data } = await axiosInstance.patch(`schools/${id}`, dto);
  return School.fromJson(data);
};

export const updateSchoolLogoImage = async (id: string, params: UpdateSchoolLogoParams): Promise<School> => {
  const formData = new FormData();
  formData.append('logo', params.logoImageFile);
  if (params.altText) {
    formData.append('altText', params.altText);
  }
  const { data } = await axiosInstance.post(`schools/${id}/logo`, formData);
  return School.fromJson(data);
};

export const getSchoolSettingBySchoolId = async (schoolId: string) => {
  const { data } = await axiosInstance.get(`/schools/${schoolId}/settings`);
  return SchoolSetting.fromJson(data);
};

export const updateSchoolSettings = async (schoolId: string, settings: Partial<SchoolSetting>) => {
  const { data } = await axiosInstance.patch(`/schools/${schoolId}/settings`, settings);
  return SchoolSetting.fromJson(data);
};
